/**
 * @flow
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Dropdown, Input, Flag, Icon } from 'semantic-ui-react';
import { keys, map, filter, get } from 'lodash';
import {
    parseNumber,
    AsYouType,
    getCountryCallingCode,
    formatNumber,
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.full.json';

/**
 * Country list
 */
const CountryList = styled(Dropdown)`
    .ui.selection.dropdown& {
        padding-left: 1em;
        padding-right: 1em;
        &::after {
            content: '\\F0D7';
            font-family: Dropdown;
            font-size: 0.85714286em;
            color: rgba(0, 0, 0, 0.87);
            opacity: 0.8;
            font-weight: 400;
        }
        .menu > .item > .flag {
            margin-right: 0;
        }
    }
`;

/**
 * Display either flag or unknown icon
 */
function CountryIcon({ name }) {
    if (name === '?') {
        return <Icon name="question" />;
    }
    return <Flag name={name} />;
}

/**
 * Setup country list options (computed once)
 */
const excludes = [
    'AC',
    'BL',
    'BQ',
    'CW',
    'GG',
    'IM',
    'JE',
    'MF',
    'SS',
    'SX',
    'TA',
    'XK',
    '001',
];

const countryOptions = map(
    filter(keys(metadata.countries), country => excludes.indexOf(country) === -1),
    country => {
        country = country.toLowerCase();
        return {
            key: country,
            value: country,
            content: <Flag name={country} />,
            text: '',
        };
    },
);

/**
 * Phone Input component
 */
function PhoneInput({ onChange, value, flag: initialFlag = 'lv', disabled, ...props }) {
    const [flag, setFlag] = useState(initialFlag);
    // Local display state — the underlying <input> is controlled by this,
    // not by the parent's value prop (which is form-managed and lags).
    const [inputValue, setInputValue] = useState(() => {
        if (value) return formatNumber(value, 'International') || value;
        return '+' + getCountryCallingCode(initialFlag.toUpperCase());
    });

    // Last value this component reported via onChange — used to tell our
    // own echoes (parent feeding the emitted value back) apart from
    // external changes (form reset()/setValue, user-lookup prefill).
    const lastEmittedRef = useRef(null);

    // Notify parent of initial value on mount
    useEffect(() => {
        const flagVal = value
            ? (parseNumber(value, { extended: true }).country || initialFlag).toLowerCase()
            : initialFlag;
        setFlag(flagVal);
        lastEmittedRef.current = inputValue;
        onChange(null, { value: inputValue });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync the display when the value prop changes EXTERNALLY (e.g. a
    // react-hook-form reset() from the Clear button or user-lookup
    // prefill). Without this the input keeps showing the stale number
    // while the form submits the new one.
    useEffect(() => {
        if (value === lastEmittedRef.current || value === inputValue) {
            return;
        }
        lastEmittedRef.current = value;
        if (value) {
            setInputValue(formatNumber(value, 'International') || value);
            setFlag(
                (parseNumber(value, { extended: true }).country || initialFlag).toLowerCase(),
            );
        } else {
            setInputValue('+' + getCountryCallingCode(initialFlag.toUpperCase()));
            setFlag(initialFlag);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    /**
     * Changes flag and country code in the input field
     */
    const handleCountryChange = useCallback(
        (e, data) => {
            const code = getCountryCallingCode(data.value.toUpperCase());
            const phone = parseNumber(inputValue, { extended: true });
            let newValue = inputValue.replace('+', '');
            if (newValue.indexOf(phone.countryCallingCode) === 0) {
                newValue = newValue.replace(phone.countryCallingCode, `+${code}`);
            } else if (newValue.length <= 3) {
                newValue = `+${code}`;
            } else {
                newValue = `+${code}${newValue}`;
            }
            newValue = formatNumber(newValue, 'International');
            setInputValue(newValue);
            lastEmittedRef.current = newValue;
            onChange(e, { value: newValue });
            setFlag(data.value.toLowerCase());
        },
        [onChange, inputValue],
    );

    /**
     * Changes flag and formats input field value
     */
    const handleInputChange = useCallback(
        (e, { value: typedValue }) => {
            const result = parseNumber(typedValue, { extended: true });
            let newFlag = result.country;
            if (!newFlag && result.countryCallingCode) {
                newFlag = get(metadata, [
                    'country_calling_codes',
                    result.countryCallingCode,
                    '0',
                ]);
            }
            setFlag((newFlag || '?').toLowerCase());
            // Skip AsYouType reformatting when deleting — reformatting changes the
            // string length which causes React to reset the cursor to end of input.
            // The next typed character will trigger formatting again.
            const isDeleting = typedValue.length < inputValue.length;
            const formatted = isDeleting ? typedValue : new AsYouType().input(typedValue);
            setInputValue(formatted);
            lastEmittedRef.current = formatted;
            onChange(e, { value: formatted });
        },
        [onChange, inputValue],
    );

    return (
        <Input
            {...props}
            value={inputValue}
            disabled={disabled}
            onChange={handleInputChange}
            label={
                <CountryList
                    options={countryOptions}
                    onChange={handleCountryChange}
                    icon={<CountryIcon name={flag} />}
                    selectOnBlur={false}
                    disabled={disabled}
                    scrolling
                    selection
                    compact
                />
            }
        />
    );
}

export default PhoneInput;
